import { z } from 'zod';

/**
 * Public marketing counters on the pre-login showcase (M16).
 *
 * Agreed at the 11.08.2026 review (row 7): show aggregate totals as marketing, but
 * **deliberately not the raw live numbers at launch** — Özcan's concern, verbatim, was
 * *"they shouldn't see 4000 auctions and 3 cars sold"*. The worry is the **ratio**, not the
 * magnitude: a young platform's true figures can read as failure to a first-time visitor.
 *
 * So each counter carries a **baseline** that is added to the real count, and an `enabled`
 * flag. Displayed = `baseline + real`, which keeps the number honest in the sense that
 * matters — it still moves with actual activity, and it never goes down — while letting an
 * operator set where the count starts from. A flat manual override would have frozen the
 * figure and needed re-editing forever; an offset needs setting once.
 *
 * Both counters default to **disabled**. A marketing number that appears on the public page
 * on its own, unattended, is exactly the accident this feature exists to prevent.
 */
export const showcaseCounterSchema = z.object({
  enabled: z.boolean(),
  /**
   * Added to the real count. Non-negative: subtracting would let the public figure fall
   * *below* reality and, worse, go backwards as the offset was tuned — a counter that
   * decreases is a story no operator wants to explain.
   */
  baseline: z.number().int().min(0).max(1_000_000),
});
export type ShowcaseCounter = z.infer<typeof showcaseCounterSchema>;

/**
 * The configured counter set. Keys are fixed rather than free-form: each one needs its own
 * aggregate query and its own translated label, so a new counter is a code change either
 * way and an open map would only invite entries nothing renders.
 *
 * - `salesHeld` — auctions that reached `CLOSED`.
 * - `vehiclesSold` — lots that ended `SOLD`.
 */
export const showcaseCountersSchema = z.object({
  salesHeld: showcaseCounterSchema,
  vehiclesSold: showcaseCounterSchema,
});
export type ShowcaseCounters = z.infer<typeof showcaseCountersSchema>;

/** The counter keys, for iterating both server- and client-side without a second literal. */
export const SHOWCASE_COUNTER_KEYS = ['salesHeld', 'vehiclesSold'] as const satisfies readonly (keyof ShowcaseCounters)[];
export type ShowcaseCounterKey = (typeof SHOWCASE_COUNTER_KEYS)[number];

/**
 * What the public showcase receives: **displayed figures only**, and only for the counters
 * an operator turned on. The baseline never leaves the Worker — publishing the offset
 * alongside the number would let any visitor subtract it back out, which would make the
 * curation worse than not doing it.
 *
 * A disabled counter is **absent**, not zero: the client hides what it isn't given, and a
 * zero would render as a real figure of nought.
 */
export const showcaseStatsSchema = z.object({
  salesHeld: z.number().int().min(0).optional(),
  vehiclesSold: z.number().int().min(0).optional(),
});
export type ShowcaseStats = z.infer<typeof showcaseStatsSchema>;

/**
 * The admin projection: the configuration **plus** the live raw counts, so the operator
 * setting a baseline can see the figure the public will get before saving it. Handing back
 * the config alone would make them do the addition in their head against numbers they
 * cannot see — which is how the embarrassing ratio ships by accident.
 */
export const showcaseCountersAdminSchema = z.object({
  counters: showcaseCountersSchema,
  /** Live counts from D1, before any baseline is applied. */
  real: z.object({
    salesHeld: z.number().int().min(0),
    vehiclesSold: z.number().int().min(0),
  }),
});
export type ShowcaseCountersAdmin = z.infer<typeof showcaseCountersAdminSchema>;
